import ApplicationController from 'javascript/controllers/application_controller';

export default class extends ApplicationController {
  static targets = [
    'eventId', // Hidden field containing the event ID
    'description', // Rich text editor containing the description
    'menu', // AI actions menu
    'button', // AI actions toggle button
  ];

  connect() {
    this.setupMenu();
    this.setupClickOutsideHandler();
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    document.removeEventListener('click', this.handleClickOutside);
  }

  setupClickOutsideHandler() {
    // Bind the handler to preserve 'this' context
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.handleClickOutside);
  }

  handleClickOutside(event) {
    // If we have a menu and it's visible
    if (
      this.hasMenuTarget &&
      !this.menuTarget.classList.contains('util-hidden')
    ) {
      // Check if click was outside both menu and button
      const clickedElement = event.target;
      const isOutsideMenu = !this.menuTarget.contains(clickedElement);
      const isOutsideButton = !this.buttonTarget.contains(clickedElement);

      if (isOutsideMenu && isOutsideButton) {
        this.menuTarget.classList.add('util-hidden');
      }
    }
  }

  setupMenu() {
    // Create menu element if it doesn't exist
    if (!this.hasMenuTarget) {
      const menu = this.buildMenu();
      // Create a wrapper div for positioning
      const wrapper = document.createElement('div');
      wrapper.style.position = 'relative';
      // Insert wrapper after the button
      this.buttonTarget.parentNode.insertBefore(
        wrapper,
        this.buttonTarget.nextSibling,
      );
      wrapper.appendChild(menu);
    }
  }

  buildMenu() {
    const menu = document.createElement('div');
    menu.setAttribute('data-target', 'description.menu');
    menu.className = 'ai-menu util-hidden';

    const menuItems = [
      { icon: '✏️', text: 'Improve writing', action: 'improve' },
      { icon: '✓', text: 'Fix spelling & grammar', action: 'fix' },
      { icon: '🔤', text: 'Translate', action: 'translate' },
      { icon: '≡', text: 'Make longer', action: 'lengthen' },
      { icon: '—', text: 'Make shorter', action: 'shorten' },
    ];

    menuItems.forEach((item) => {
      const menuItem = document.createElement('button');
      menuItem.className = 'ai-menu-item';
      menuItem.setAttribute('data-action', `click->description#${item.action}`);
      menuItem.innerHTML = `
        <span class="ai-menu-item__icon">${item.icon}</span>
        <span class="ai-menu-item__text">${item.text}</span>
      `;
      menu.appendChild(menuItem);
    });

    return menu;
  }

  toggleMenu(event) {
    // Prevent this click from being caught by the document click handler
    event.stopPropagation();
    if (this.hasMenuTarget) {
      this.menuTarget.classList.toggle('util-hidden');
    }
  }

  // AI Actions
  async improve(event) {
    event.preventDefault();
    await this.generateDescription('improve');
  }

  async fix(event) {
    event.preventDefault();
    await this.generateDescription('fix');
  }

  async translate(event) {
    event.preventDefault();
    await this.generateDescription('translate');
  }

  async lengthen(event) {
    event.preventDefault();
    await this.generateDescription('lengthen');
  }

  async shorten(event) {
    event.preventDefault();
    await this.generateDescription('shorten');
  }

  async generateDescription(action) {
    this.menuTarget.classList.add('util-hidden');
    this.buttonTarget.disabled = true;
    this.buttonTarget.textContent = 'Generating...';

    try {
      const currentDescription = this.getCurrentDescription();

      const formData = {
        event: {
          id: this.eventIdTarget.value,
          action: action,
          current_description: currentDescription,
        },
      };

      const response = await fetch(this.data.get('generate-url-value'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate description');
      }

      const data = await response.json();
      if (data.description) {
        this.updateDescription(data.description);
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert(error.message);
    } finally {
      this.buttonTarget.disabled = false;
      this.buttonTarget.textContent = 'Ask AI';
    }
  }

  getCurrentDescription() {
    const editor = this.findQuillEditor();
    if (editor) {
      return editor.root.innerHTML;
    }
    return this.descriptionTarget.value;
  }

  updateDescription(newDescription) {
    const editor = this.findQuillEditor();
    if (editor) {
      // Update Quill editor content
      editor.clipboard.dangerouslyPasteHTML(newDescription);
      // The text-change event will automatically update the hidden field
    } else {
      // Fallback to regular textarea
      this.descriptionTarget.value = newDescription;
      this.descriptionTarget.dispatchEvent(
        new Event('input', { bubbles: true }),
      );
    }
  }

  findQuillEditor() {
    if (window.Quill) {
      const editorNode = this.descriptionTarget.querySelector(
        '.rich-textarea__editor',
      );
      return editorNode ? window.Quill.find(editorNode) : null;
    }
    return null;
  }
}
